// sygnaly.ts — ANATOMIA INSIGHTU jako dane, nie jako układ graficzny.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
//  PO CO TEN PLIK ISTNIEJE
//
//  Ekran „Insighty" pokazuje sygnały z DWÓCH niezależnych silników: reguł na daniach (CRM) oraz
//  zbieżności sprzedaż ↔ dostawy (złączenie po stronie przeglądarki, lib/crossSystem.ts).
//  Gdyby każdy z nich rysował własną kartę, czytelnik uczyłby się układu od nowa przy każdym
//  sygnale — a to jest dokładnie ten koszt, którego kontrakt anatomii zabrania.
//
//  Dlatego oba silniki są tu SPROWADZANE DO JEDNEGO KSZTAŁTU:
//      CO SIĘ STAŁO → DLACZEGO WAŻNE → DOWÓD → CO SPRAWDZIĆ
//  Kolejność jest własnością komponentu (KartaSygnalu), a nie treści — dzięki temu nie da się jej
//  przestawić dla pojedynczego sygnału, nawet przez nieuwagę.
//
//  ŻADNE POLE NIE JEST TU WYMYŚLANE. Każdy łańcuch powstaje z pól, które przyszły z API albo
//  zostały policzone w lib/crossSystem.ts. Jeżeli czegoś w odpowiedzi nie ma — nie ma tego także
//  na ekranie. Zmyślony szczegół („wszystkie z jednej restauracji") brzmi wiarygodnie i właśnie
//  dlatego jest gorszy niż jego brak: ktoś na jego podstawie zadzwoni.
// ══════════════════════════════════════════════════════════════════════════════════════════════

import type { Dowod } from '@/components/SzufladaDowodu';
import type { Insight, InsightType } from '@/lib/api/analytics';
import type { Coincidence, JoinReport } from '@/lib/crossSystem';
import { CROSS_ENGINE_VERSION, DEFAULT_THRESHOLDS } from '@/lib/crossSystem';
import { DATA_QUALITY_LABEL, type DataQuality } from '@/lib/dataQuality';
import { formatCount, formatDelta, formatGrosze, formatRange } from '@/lib/format';
import type { MetricBasis } from '@/lib/metricBasis';
import { zawieraSuperlatyw } from '@/lib/superlatyw';
import { klasyfikacjaDrive, swiezoscDrive, swiezoscMiedzysystemowa } from '@/lib/pochodzenieDanych';

/*
 * ŚWIEŻOŚĆ ŹRÓDEŁ — dwie różne prawdy, obie wpisane wprost.
 * Sprzedaż CRM nie raportuje momentu odczytu, więc każda liczba stamtąd ma świeżość NIEUSTALONĄ.
 * Wpisanie w to miejsce stempla „odczytano teraz" byłoby zmyśleniem najbardziej kosztownego rodzaju:
 * wyglądałoby na kontrolę jakości, a nie byłoby nią.
 */
export const SWIEZOSC_CRM = 'NIEUSTALONA — źródło nie raportuje momentu odczytu';
/* Wyliczane, nie wpisane: na produkcji to zdanie byłoby nieprawdziwe. */
export const SWIEZOSC_DRIVE = swiezoscDrive();

/**
 * Podstawa metryki silnika dań (ADR-001/002/003).
 * Wolumen to WIERSZE POZYCJI, nie zamówienia i nie porcje — silnik liczy `COUNT(*)` pozycji,
 * bo ilość bywa NULL-em. Mylenie tych trzech było realnym defektem starego panelu.
 */
export const PODSTAWA_SILNIKA_DAN: MetricBasis = {
  value: 'LIST_VALUE',
  volume: 'ORDER_ITEM_ROWS',
  time: 'DELIVERY_DATE',
};

/** Nazwy reguł po polsku. Klucze zostają angielskie — są częścią kontraktu API. */
export const ETYKIETA_TYPU: Record<InsightType, string> = {
  TOP_DISH: 'Największa wartość katalogowa',
  FASTEST_GROWING_DISH: 'Największy wzrost',
  FASTEST_DECLINING_DISH: 'Największy spadek',
  LOW_PERFORMER: 'Słaby wynik',
  PRICE_CHANGE_SIGNAL: 'Sygnał cenowy',
  HIGH_REPEAT_PRODUCT: 'Wysoka powtarzalność',
  SCHOOL_OUTLIER: 'Odstępstwo szkolne',
};

/*
 * Tłumaczenie kluczy dowodu. Ta mapa istnieje także w components/InsightCard.tsx i NIE jest stamtąd
 * eksportowana; tamten plik należy do innego strumienia prac, więc go nie ruszam. Duplikat jest
 * świadomy i ograniczony do warstwy prezentacji — klucze i liczby pochodzą z jednego źródła (API),
 * rozjechać się mogą wyłącznie polskie etykiety, a nie wartości.
 */
const ETYKIETA_DOWODU: Record<string, string> = {
  units: 'pozycji sprzedanych',
  units_current: 'pozycji w oknie bieżącym',
  units_previous: 'pozycji w oknie porównawczym',
  orders: 'zamówień',
  buyers: 'kupujących',
  repeat_buyers: 'kupujących powracających',
  days: 'dni ze sprzedażą',
  schools: 'szkół',
  restaurants: 'restauracji',
  revenue_list_grosze: 'wartość katalogowa (grosze)',
  school_total_units: 'pozycji w tej szkole (łącznie)',
  other_schools: 'porównywanych szkół',
  unit_price_current_grosze: 'średnia cena teraz (grosze)',
  unit_price_previous_grosze: 'średnia cena wcześniej (grosze)',
};

/*
 * CZEGO Z TEJ LICZBY NIE WYNIKA — osobno dla KAŻDEJ reguły.
 *
 * Jedno ogólne zdanie („to nie jest dowód przyczynowy") byłoby wygodne i bezużyteczne: czytelnik
 * odczytuje je raz i przestaje widzieć. Konkret jest inny dla każdej reguły, bo każda reguła myli
 * się inaczej — i tylko konkret zatrzymuje przed złą decyzją.
 */
const CZEGO_NIE_WYNIKA: Record<InsightType, string> = {
  TOP_DISH:
    'Największa wartość katalogowa to NIE największy zysk ani największa marża — kosztu składników nie ma w tym zbiorze. ' +
    'Nie wynika z niej też, że danie jest najczęściej zamawiane: pozycja droższa wyprzedzi tańszą przy mniejszej liczbie sztuk.',
  FASTEST_GROWING_DISH:
    'Wzrost policzony na dwóch oknach nie mówi, czy się utrzyma, ani skąd się wziął. Nie wynika z niego również, że przybyło ' +
    'kupujących — ten sam zbiór osób mógł kupić więcej. Liczba kupujących jest osobnym polem dowodu i to ona na to odpowiada.',
  FASTEST_DECLINING_DISH:
    'Spadek nie wskazuje przyczyny: w dowodzie widać liczbę dni ze sprzedażą i liczbę szkół, a to one najczęściej różnią się ' +
    'między oknami. Z tej liczby nie wynika też utrata przychodu — podstawą są ceny katalogowe, nie kwoty faktycznie pobrane.',
  LOW_PERFORMER:
    'Słaby wynik w oknie nie jest rekomendacją zdjęcia dania z karty. Zbiór nie zawiera kosztu, marży ani informacji, przez ile ' +
    'dni okna danie w ogóle było zamawiane — a przy krótkiej obecności niska sprzedaż jest arytmetyką, nie sygnałem.',
  PRICE_CHANGE_SIGNAL:
    'Zmiana ŚREDNIEJ ceny jednostkowej nie dowodzi zmiany cennika. Ta sama pozycja sprzedana w innym miksie szkół daje inną ' +
    'średnią przy niezmienionych cenach. Nie wynika z niej również, ile klient zapłacił — to są ceny katalogowe, przed zniżką szkolną.',
  HIGH_REPEAT_PRODUCT:
    'Powtarzalność liczy, że ten sam identyfikator kupującego wystąpił ponownie w oknie. Nie wynika z niej lojalność, zadowolenie ' +
    'ani preferencja — także nie to, że danie było wybierane spośród innych: nie wiemy, co jeszcze było w koszyku.',
  SCHOOL_OUTLIER:
    'Niższy udział dania w jednej szkole nie jest dowodem problemu tej szkoły ani jej restauracji. Porównywany jest UDZIAŁ pozycji, ' +
    'a nie sprzedaż na ucznia — szkoła o innym profilu zamówień da ten sam sygnał bez żadnej nieprawidłowości.',
};

/** Zdanie o przyczynowości — na każdym sygnale, bo każdy sygnał jest zbieżnością, nie mechanizmem. */
const PRZYCZYNOWOSC_SILNIK_DAN =
  'Związek przyczynowy nieustalony — to jest współwystępowanie w czasie, nie dowód. Reguła zestawia dwa okna i nic nie wie ' +
  'o tym, co działo się między nimi.';

const PRZYCZYNOWOSC_ZBIEZNOSC =
  'Związek przyczynowy nieustalony. Kierunek zależności może być odwrotny, przyczyna może być wspólna dla obu wielkości, ' +
  'a zbieżność może być przypadkiem — przy kilkudziesięciu szkołach jedna taka para pojawi się sama z siebie.';

export interface WierszDowodu {
  etykieta: string;
  wartosc: string;
}

export interface DowodPrzycisk {
  etykieta: string;
  dowod: Dowod;
}

export interface KrokSprawdzenia {
  etykieta: string;
  tresc: string;
}

/** Jeden sygnał w kształcie, który KartaSygnalu potrafi narysować w stałej kolejności. */
export interface Sygnal {
  klucz: string;
  rodzaj: string;
  tytul: string;
  coSieStalo: string;
  dlaczegoWazne: string;
  wierszeDowodu: readonly WierszDowodu[];
  podstawa: MetricBasis;
  okno: string;
  oknoPorownawcze: string;
  silnik: string;
  /** Liczba próby — pokazywana PRZY sygnale, nie w stopce ekranu. */
  proba: string;
  /** Co ta próba oznacza (albo: że źródło tego nie deklaruje). */
  probaOpis: string;
  jakosc: DataQuality;
  jakoscPowod?: string;
  coSprawdzic: readonly KrokSprawdzenia[];
  /** Sprzeczności wykryte na froncie — nie ukrywamy sygnału, oznaczamy go. */
  ostrzezenia: readonly string[];
  dowody: readonly DowodPrzycisk[];
  przyczynowosc: string;
}

function liczbaLubBrak(v: number | null | undefined): string {
  return v === null || v === undefined ? 'brak danych' : formatCount(v);
}

function procentLubBrak(v: number | null | undefined): string {
  return v === null || v === undefined ? 'brak danych' : `${v}%`;
}

function punktyLubBrak(v: number | null | undefined): string {
  return v === null || v === undefined ? 'brak danych' : `${v} p.p.`;
}

/**
 * SYGNAŁ Z SILNIKA REGUŁ NA DANIACH.
 *
 * `caveats` przychodzą z backendu (`DishSalesResponse.caveats`) i wędrują do ograniczeń dowodu
 * bez przepisywania — zastrzeżenie napisane przez autora liczby jest warte więcej niż jego
 * streszczenie zrobione we froncie.
 */
export function sygnalZInsightu(insight: Insight, caveats: readonly string[]): Sygnal {
  const okno = formatRange(insight.time_window.date_from, insight.time_window.date_to);
  const oknoPorownawcze = formatRange(insight.comparison_window.date_from, insight.comparison_window.date_to);

  const wierszeDowodu: WierszDowodu[] = Object.entries(insight.evidence).map(([klucz, wartosc]) => ({
    etykieta: ETYKIETA_DOWODU[klucz] ?? klucz,
    wartosc: liczbaLubBrak(wartosc),
  }));

  /*
   * STRAŻNIK SUPERLATYWU (warunek konieczny).
   * Zdanie „największy" jest twierdzeniem o SKRAJNOŚCI w zbiorze; przy próbie mniejszej niż dwa
   * porównywalne byty nie ma zbioru, w którym można być skrajnym. Front nie może poprawić liczby
   * z backendu, ale jest ostatnim miejscem, w którym da się złapać zdanie sprzeczne z własnym dowodem.
   */
  const ostrzezenia: string[] = [];
  if (zawieraSuperlatyw(`${insight.title} ${insight.fact}`) && insight.sample_size < 2) {
    ostrzezenia.push(
      `Ten sygnał twierdzi o skrajności („największy", „najwyższy") przy próbie ${insight.sample_size}. ` +
        'Superlatyw wymaga co najmniej dwóch porównywalnych bytów — czytaj to jako obserwację o pojedynczym przypadku, nie jako rekord.',
    );
  }

  const probaOpis =
    'Silnik podaje wielkość próby samą liczbą, bez jednostki — z tego pola nie wynika, czy to zamówienia, ' +
    'pozycje czy kupujący. Jednostki wolumenu szukaj w wierszach dowodu poniżej.';

  const dowod: Dowod = {
    tytul: insight.title,
    etykietaWartosci: 'Próba zadeklarowana przez silnik',
    wartosc: formatCount(insight.sample_size),
    podstawa: PODSTAWA_SILNIKA_DAN,
    zrodlo: `CRM /analytics/dish-sales → silnik reguł ${insight.engine_version}`,
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: SWIEZOSC_CRM,
    oknoBiezace: okno,
    oknoPorownawcze,
    wyliczenie:
      `Deterministyczna reguła „${ETYKIETA_TYPU[insight.type]}" (${insight.engine_version}): zestawienie okna bieżącego ` +
      'z równie długim oknem bezpośrednio poprzedzającym. Front NIE przelicza tych liczb — pokazuje wartości zwrócone przez ' +
      'silnik razem z jego własnym dowodem, żeby dało się je sprawdzić, a nie tylko im uwierzyć.',
    szczegoly: [
      ['Reguła', insight.type],
      ['Byt', insight.entity_name ?? `${insight.entity_type} #${insight.entity_id ?? 'bez identyfikatora'}`],
      ...wierszeDowodu.map((w) => [w.etykieta, w.wartosc] as const),
    ],
    proba: formatCount(insight.sample_size),
    jakoscDanych: DATA_QUALITY_LABEL[insight.data_quality],
    ograniczenia: [
      probaOpis,
      'Silnik zwraca wyłącznie obserwacje ponad progami istotności. Sygnał poniżej progu nie znika z danych — po prostu ' +
        'nie ma go na tym ekranie.',
      ...ostrzezenia,
      ...caveats,
    ],
    czegoNieWynika: CZEGO_NIE_WYNIKA[insight.type],
  };

  const coSprawdzic: KrokSprawdzenia[] = [
    { etykieta: 'Następny krok', tresc: insight.recommended_next_step },
  ];
  if (insight.hypothesis) coSprawdzic.push({ etykieta: 'Hipoteza (nie ustalenie)', tresc: insight.hypothesis });
  if (insight.suggested_test) coSprawdzic.push({ etykieta: 'Test, który by to rozstrzygnął', tresc: insight.suggested_test });

  return {
    klucz: insight.insight_id,
    rodzaj: ETYKIETA_TYPU[insight.type],
    tytul: insight.title,
    coSieStalo: insight.fact,
    dlaczegoWazne: insight.why_it_matters,
    wierszeDowodu,
    podstawa: PODSTAWA_SILNIKA_DAN,
    okno,
    oknoPorownawcze,
    silnik: insight.engine_version,
    proba: formatCount(insight.sample_size),
    probaOpis,
    jakosc: insight.data_quality,
    coSprawdzic,
    ostrzezenia,
    dowody: [{ etykieta: 'Skąd ten sygnał', dowod }],
    przyczynowosc: PRZYCZYNOWOSC_SILNIK_DAN,
  };
}

/**
 * SYGNAŁ ZE ZBIEŻNOŚCI SPRZEDAŻ ↔ DOSTAWY.
 *
 * Dwie liczby z DWÓCH systemów o różnej klasyfikacji źródła, więc dwa osobne dowody: kwota idzie
 * z CRM (świeżość nieustalona), punktualność z Drive (zbiór kontrolowany TEST). Jeden wspólny dowód
 * musiałby skłamać w jednym z tych dwóch wierszy.
 */
export function sygnalZeZbieznosci(
  item: Coincidence,
  okno: { date_from: string; date_to: string },
  porownanie: { date_from: string; date_to: string },
): Sygnal {
  const oknoLabel = formatRange(okno.date_from, okno.date_to);
  const porownanieLabel = formatRange(porownanie.date_from, porownanie.date_to);
  const nazwa = item.school_name ?? `Szkoła #${item.school_id}`;

  const dowodSprzedazy: Dowod = {
    tytul: `Zmiana sprzedaży — ${nazwa}`,
    etykietaWartosci: 'Zmiana wartości katalogowej',
    wartosc: formatDelta(item.sales_delta_pct),
    podstawa: { value: 'LIST_VALUE', volume: 'ORDER_COUNT', time: 'DELIVERY_DATE' },
    zrodlo: 'CRM /analytics/school-sales → suma po szkole, okno bieżące i bezpośrednio poprzedzające',
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: SWIEZOSC_CRM,
    oknoBiezace: oknoLabel,
    oknoPorownawcze: porownanieLabel,
    wyliczenie: '(wartość w oknie − wartość w oknie poprzednim) ÷ wartość w oknie poprzednim',
    szczegoly: [
      ['Wartość w oknie', formatGrosze(item.sales_current_grosze)],
      ['Wartość w oknie poprzednim', formatGrosze(item.sales_previous_grosze)],
      ['Zamówienia teraz', formatCount(item.orders_current)],
      ['Zamówienia wcześniej', formatCount(item.orders_previous)],
      // Progi czytane z DEFAULT_THRESHOLDS, a nie przepisane — inaczej opis rozjechałby się z regułą
      // przy pierwszej zmianie progu i ekran zacząłby opisywać obliczenie, którego nikt nie wykonał.
      [
        'Próg reguły',
        `spadek co najmniej ${DEFAULT_THRESHOLDS.minSalesDropPct}% i co najmniej ${DEFAULT_THRESHOLDS.minOrders} zamówień w oknie`,
      ],
    ],
    proba: `${formatCount(item.orders_current)} zamówień w oknie`,
    czegoNieWynika:
      'To NIE jest spadek przychodu: podstawą są ceny katalogowe przed zniżką szkolną, a kwoty faktycznie pobranej źródło nie ' +
      'podaje na tym ziarnie. Nie wynika z tego również, że sprzedaż spadła „naprawdę" — przy nieustalonej świeżości źródła ' +
      'nie wiadomo, czy oba okna zostały odczytane w tym samym momencie.',
  };

  const dowodPunktualnosci: Dowod = {
    tytul: `Zmiana punktualności — ${nazwa}`,
    etykietaWartosci: 'Zmiana udziału dostaw na czas',
    wartosc: punktyLubBrak(item.on_time_delta_points),
    podstawa: { time: 'DELIVERY_DATE' },
    zrodlo: 'Drive /analytics/delivery-by-school → utrwalony status dostawy',
    klasyfikacja: klasyfikacjaDrive(),
    swiezosc: SWIEZOSC_DRIVE,
    oknoBiezace: oknoLabel,
    oknoPorownawcze: porownanieLabel,
    wyliczenie: 'udział dostaw w oknie tolerancji teraz − ten sam udział w oknie poprzednim',
    szczegoly: [
      ['Na czas teraz', procentLubBrak(item.on_time_current_pct)],
      ['Na czas wcześniej', procentLubBrak(item.on_time_previous_pct)],
      ['Dostawy w oknie', formatCount(item.deliveries)],
      ['Średnie odchylenie teraz', item.avg_delta_current_min === null ? 'brak danych' : `${item.avg_delta_current_min} min`],
      [
        'Próg reguły',
        `pogorszenie co najmniej o ${DEFAULT_THRESHOLDS.minOnTimeDropPoints} p.p. i co najmniej ` +
          `${DEFAULT_THRESHOLDS.minDeliveries} dostaw w oknie`,
      ],
    ],
    proba: `${formatCount(item.deliveries)} dostaw w oknie`,
    jakoscDanych: item.data_quality_reason,
    czegoNieWynika:
      'Dostawa „przed czasem" nie jest tu sukcesem i nie wlicza się do punktualności — przyjazd przed otwarciem stołówki potrafi ' +
      'znaczyć, że jedzenie stało. Z tej liczby nie wynika też nic o przyczynie spóźnień: w zbiorze nie ma ani kierowcy, ani trasy.',
  };

  const dowodZlaczenia: Dowod = {
    tytul: `Złączenie dla szkoły — ${nazwa}`,
    etykietaWartosci: 'Klucz złączenia',
    wartosc: `tuszama_id ${item.school_id}`,
    zrodlo: 'lib/crossSystem.ts → złączenie po tuszama_id, wykonane w przeglądarce (ADR-002)',
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: swiezoscMiedzysystemowa(),
    oknoBiezace: oknoLabel,
    oknoPorownawcze: porownanieLabel,
    wyliczenie:
      'Szkoła wchodzi do wyniku wtedy i tylko wtedy, gdy ma wiersz po OBU stronach w OBU oknach. Identyfikator jest ' +
      'normalizowany, bo CRM oddaje go jako łańcuch (BIGINT), a Drive jako liczbę (INTEGER) — bez tego złączenie po cichu ' +
      'nie dopasowuje niczego.',
    szczegoly: [
      ['Silnik złączenia', CROSS_ENGINE_VERSION],
      ['Jakość', DATA_QUALITY_LABEL[item.data_quality]],
    ],
    jakoscDanych: item.data_quality_reason,
    czegoNieWynika:
      'Dopasowanie jest po SZKOLE i po OKNIE, nie po zamówieniu ani po dniu. Z tego, że oba wiersze dotyczą tej samej szkoły, ' +
      'nie wynika, że dotyczą tych samych dostaw co zamówienia — dzienne pokrycie nie jest tu sprawdzane.',
  };

  return {
    klucz: `zbieznosc-${item.school_id}`,
    rodzaj: 'Zbieżność sprzedaż ↔ dostawy',
    tytul: item.city ? `${nazwa} · ${item.city}` : nazwa,
    coSieStalo:
      `Wartość sprzedaży zmieniła się o ${formatDelta(item.sales_delta_pct)} ` +
      `(${formatGrosze(item.sales_previous_grosze)} → ${formatGrosze(item.sales_current_grosze)}), a udział dostaw na czas ` +
      `o ${punktyLubBrak(item.on_time_delta_points)} (${procentLubBrak(item.on_time_previous_pct)} → ` +
      `${procentLubBrak(item.on_time_current_pct)}). Obie zmiany wypadły w tym samym oknie i w tej samej szkole.`,
    dlaczegoWazne:
      'To jedyne miejsce, w którym Control Center zestawia dwa systemy o rozłącznych bazach użytkowników. Każdy z nich osobno ' +
      'pokazuje tu spokojny wykres — para wychodzi dopiero ze złączenia. Jednocześnie jest to najbardziej kruchy wynik na ekranie: ' +
      'stoi na dopasowaniu identyfikatorów szkół, którego wiarygodność jest opisana obok.',
    wierszeDowodu: [
      { etykieta: 'zamówienia teraz / wcześniej', wartosc: `${formatCount(item.orders_current)} / ${formatCount(item.orders_previous)}` },
      { etykieta: 'dostawy w oknie', wartosc: formatCount(item.deliveries) },
      { etykieta: 'zmiana sprzedaży', wartosc: formatDelta(item.sales_delta_pct) },
      { etykieta: 'zmiana punktualności', wartosc: punktyLubBrak(item.on_time_delta_points) },
      {
        etykieta: 'zmiana średniego odchylenia',
        wartosc: item.avg_delta_change_min === null ? 'brak danych' : `${item.avg_delta_change_min} min`,
      },
    ],
    podstawa: { value: 'LIST_VALUE', volume: 'ORDER_COUNT', time: 'DELIVERY_DATE' },
    okno: oknoLabel,
    oknoPorownawcze: porownanieLabel,
    silnik: CROSS_ENGINE_VERSION,
    proba: `${formatCount(item.orders_current)} zam. · ${formatCount(item.deliveries)} dost.`,
    probaOpis:
      'Próba jest podwójna, bo wynik stoi na dwóch zbiorach naraz: zamówieniach w oknie (CRM) i dostawach w oknie (Drive). ' +
      'Słabszy z nich decyduje o wartości całego sygnału.',
    jakosc: item.data_quality,
    jakoscPowod: item.data_quality_reason,
    coSprawdzic: [
      {
        etykieta: 'Następny krok',
        tresc:
          'Sprawdzić, czy zbieżność utrzyma się w kolejnym oknie tej samej długości. Jeżeli zniknie — była zbiegiem okoliczności ' +
          'i nie ma czego wyjaśniać.',
      },
      {
        etykieta: 'Czego ten ekran nie zestawi',
        tresc:
          'Porównanie z pozostałymi szkołami tej samej restauracji wymaga pola, którego w tym złączeniu nie ma — wynik nie niesie ' +
          'restauracji. Trzeba to zestawić po stronie CRM, na ekranie sprzedaży restauracji.',
      },
    ],
    ostrzezenia: [],
    dowody: [
      { etykieta: 'Skąd zmiana sprzedaży', dowod: dowodSprzedazy },
      { etykieta: 'Skąd zmiana punktualności', dowod: dowodPunktualnosci },
      { etykieta: 'Skąd to złączenie', dowod: dowodZlaczenia },
    ],
    przyczynowosc: PRZYCZYNOWOSC_ZBIEZNOSC,
  };
}

/** Dowód pod raportem złączenia — liczby te same, które widać w karcie „Wiarygodność złączenia". */
export function dowodZlaczenia(report: JoinReport, okno: string, porownanie: string): Dowod {
  return {
    tytul: 'Wiarygodność złączenia CRM ↔ Drive',
    etykietaWartosci: 'Udział dopasowanych szkół',
    wartosc: report.match_pct === null ? null : `${report.match_pct}%`,
    podstawa: { time: 'DELIVERY_DATE' },
    zrodlo: 'CRM /analytics/school-sales + Drive /analytics/delivery-by-school, złączenie w przeglądarce (lib/crossSystem.ts)',
    klasyfikacja: 'SWIEZOSC_NIEUSTALONA',
    swiezosc: swiezoscMiedzysystemowa(),
    oknoBiezace: okno,
    oknoPorownawcze: porownanie,
    wyliczenie:
      'dopasowane identyfikatory ÷ liczniejszy z dwóch zbiorów (szkoły ze sprzedażą, szkoły z dostawami). ' +
      'Poniżej 60% złączenie jest oznaczone jako CZĘŚCIOWE, przy zerowym dopasowaniu — jako ZABLOKOWANE.',
    szczegoly: [
      ['Status', report.status],
      ['Szkoły ze sprzedażą', formatCount(report.sales_schools)],
      ['Szkoły z dostawami', formatCount(report.delivery_schools)],
      ['Dopasowane', formatCount(report.matched)],
      ['Tylko sprzedaż', formatCount(report.sales_only)],
      ['Tylko dostawy', formatCount(report.delivery_only)],
      ['Dostawy bez identyfikatora szkoły', formatCount(report.delivery_rows_without_id)],
    ],
    proba: `${formatCount(report.matched)} dopasowanych szkół`,
    ograniczenia: [
      report.reason,
      'Szkoła obecna tylko po jednej stronie NIE jest błędem złączenia — może po prostu nie mieć dostaw albo nie mieć sprzedaży ' +
        'w tym oknie. Błędem jest dopiero niskie pokrycie, bo wtedy wynik dotyczy mniejszości sieci.',
    ],
    czegoNieWynika:
      'Wysokie dopasowanie NIE znaczy, że obie strony opisują te same zamówienia ani te same dni — złączenie jest po szkole i po ' +
      'oknie. Nie wynika z niego również poprawność samych liczb: dobrze dopasowane mogą być dwa źle policzone zbiory.',
  };
}
